import type { AttackCommand, MatchState } from "../../../../schemas"
import type { CommandResult } from "../state"
import {
  appendLog,
  applyDamageToCC,
  applyDamageToInstance,
  checkWinCondition,
  drawCards,
  err,
  getOtherSeatId,
  getSeat,
  refillMercRow,
  resolveLethalDamage,
  seatHasGuard,
  updateInstance,
  updateSeat,
} from "../state"

export function handleAttack(
  state: MatchState,
  cmd: AttackCommand,
): CommandResult {
  const seatId = cmd.seatId
  const seat = getSeat(state, seatId)
  const { attackerId, target } = cmd.payload

  const attacker = state.instances[attackerId]
  if (!attacker)
    return err("ATTACK_UNKNOWN_ATTACKER", `Unknown attacker ${attackerId}.`)

  const isActiveAttacker = seat.zones.active === attackerId
  const isSupportAttacker = seat.zones.support.includes(attackerId)
  if (
    !isActiveAttacker &&
    !(isSupportAttacker && attacker.combatState.hasRanged)
  ) {
    return err(
      "ATTACK_NOT_IN_ATTACK_POSITION",
      "Only the active unit or a support unit with Ranged can attack.",
    )
  }

  // Must be able to attack this turn
  if (
    !attacker.status.canAttackThisTurn ||
    attacker.status.hasAttackedThisTurn ||
    attacker.status.jammedForTurnNumber === state.turn.number
  ) {
    return err(
      "ATTACK_ALREADY_ATTACKED",
      "This unit cannot attack again this turn.",
    )
  }

  const baseAttack = attacker.status.remainingAttack ?? attacker.currentAttack
  const coordinatedBonus = isActiveAttacker
    ? getCoordinatedStrikeBonus(state, seatId)
    : 0
  const attackValue = baseAttack + coordinatedBonus
  const defenderSeatId = target.seatId
  const opposingGuard = seatHasGuard(defenderSeatId, state)

  let s = state
  let destroyedUnit = false

  if (target.type === "central_command") {
    // Guard check
    if (opposingGuard && !attacker.combatState.ignoresGuard) {
      return err(
        "ATTACK_BLOCKED_BY_GUARD",
        "Cannot target Central Command while opponent has a Guard unit. Remove the Guards or bypass them.",
      )
    }
    s = applyDamageToCC(s, defenderSeatId, attackValue)
    s = appendLog(s, seatId, "attack_cc", {
      attackerId,
      targetSeat: defenderSeatId,
      damage: attackValue,
    })
  } else {
    // Unit attack
    const targetId = target.instanceId
    if (!targetId)
      return err("ATTACK_NO_TARGET_ID", "Unit attack requires instanceId.")

    const targetInst = s.instances[targetId]
    if (!targetInst)
      return err(
        "ATTACK_UNKNOWN_TARGET",
        `Unknown target instance ${targetId}.`,
      )

    // Validate target is in a targetable zone
    const defSeat = getSeat(s, defenderSeatId)
    const isActive = defSeat.zones.active === targetId
    const isSupport = defSeat.zones.support.includes(targetId)
    if (!isActive && !isSupport) {
      return err(
        "ATTACK_INVALID_TARGET_ZONE",
        "Target unit is not in an attackable zone.",
      )
    }
    const isGuardTarget = targetInst.combatState.hasGuard
    if (opposingGuard && !attacker.combatState.ignoresGuard && !isGuardTarget) {
      return err(
        "ATTACK_BLOCKED_BY_GUARD",
        "You must attack a Guard unit first unless you bypass Guard.",
      )
    }
    if (
      isSupport &&
      opposingGuard &&
      !attacker.combatState.ignoresGuard &&
      !isGuardTarget
    ) {
      return err(
        "ATTACK_SUPPORT_REQUIRES_BYPASS",
        "You must attack a Guard unit first unless you bypass Guard.",
      )
    }

    const currentHealthBefore = targetInst.currentHealth
    const armorReduction = targetInst.combatState.hasArmor ? 1 : 0
    s = applyDamageToInstance(s, targetId, attackValue)
    destroyedUnit = (s.instances[targetId]?.currentHealth ?? 1) <= 0
    s = appendLog(s, seatId, "attack_unit", {
      attackerId,
      targetId,
      damage: attackValue,
    })

    if (attacker.combatState.breaksThrough && destroyedUnit) {
      const spentAttack = Math.min(
        attackValue,
        currentHealthBefore + armorReduction,
      )
      const remainingAttack = Math.max(0, attackValue - spentAttack)
      s = updateInstance(s, attackerId, (inst) => ({
        ...inst,
        status: {
          ...inst.status,
          canAttackThisTurn: remainingAttack > 0,
          hasAttackedThisTurn: remainingAttack <= 0,
          remainingAttack,
        },
      }))
    }
  }

  if (attacker.combatState.hasFocus) {
    s = updateSeat(s, seatId, (seatState) => ({
      ...seatState,
      resources: {
        ...seatState.resources,
        currentEnergy: seatState.resources.currentEnergy + 1,
      },
    }))
  }

  if (destroyedUnit && attacker.combatState.hasKo) {
    s = drawCards(s, seatId, 1)
  }

  // Mark attacker as having attacked
  if (
    !attacker.combatState.breaksThrough ||
    target.type === "central_command" ||
    !destroyedUnit
  ) {
    s = updateInstance(s, attackerId, (inst) => ({
      ...inst,
      status: {
        ...inst.status,
        canAttackThisTurn: false,
        hasAttackedThisTurn: true,
        remainingAttack: 0,
      },
    }))
  }

  // State-based: resolve lethal damage
  s = resolveLethalDamage(s)
  s = checkWinCondition(s)

  // Refill any empty merc row slots
  const otherSeatId = getOtherSeatId(seatId)
  s = refillEmptyMercRow(s, seatId)
  s = refillEmptyMercRow(s, otherSeatId)

  return { ok: true, state: s }
}

function getCoordinatedStrikeBonus(
  state: MatchState,
  seatId: "seat_a" | "seat_b",
): number {
  const seat = getSeat(state, seatId)
  return seat.zones.support.filter(
    (id) => state.instances[id]?.combatState.hasCoordinatedStrike,
  ).length
}

function refillEmptyMercRow(
  state: MatchState,
  seatId: "seat_a" | "seat_b",
): MatchState {
  let s = state
  while (
    getSeat(s, seatId).zones.mercRow.length < 3 &&
    getSeat(s, seatId).zones.supply.length > 0
  ) {
    s = refillMercRow(s, seatId)
  }
  return s
}
